import { ReactNode } from "react";

import { css, keyframes } from "@compiled/react";
// @ts-ignore
import { useInView } from "react-intersection-observer";
import { FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlaskit/react-ufo/segment";

import { Adg4Button } from "@/components/adg4/button/src";
import { Picture } from "@/components/wpl/image/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

const FutureOfTeamworkHeading = (str: ReactNode) => (
    <span className="h1">{str}</span>
);

const HelpUsShapeHeading = (str: ReactNode) => (
    <span css={reducedFontWeightStyle} className="h1 reduced-font-weight">
        {str}
    </span>
);

export const FutureOfTeamwork = () => {
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();

    return (
        <UFOSegment name="homepage-v2-futureOfTeamwork">
            <section css={[sectionStyles]} ref={scrollRef}>
                <Row layout="8">
                    <div css={contentWrapperStyles} className="content-wrapper">
                        <Picture
                            image={{
                                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/2878633782yw0cnv532u77430xi7x15x.svg",
                                alt: "",
                                width: 93,
                                height: 91
                            }}
                            css={[
                                svgStyles,
                                bigStarStyles,
                                sectionIsVisible && bigStarAnimation
                            ]}
                            className="big-star"
                        />
                        <Picture
                            image={{
                                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/a8r57w04r3t6j6087ewxa4bytiqr43me.svg",
                                alt: "",
                                width: 49,
                                height: 51
                            }}
                            css={[
                                svgStyles,
                                smStarStyles,
                                sectionIsVisible && smStarAnimation
                            ]}
                            className="sm-star"
                        />
                        <Picture
                            image={{
                                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/igdrq2i5082s2p672q63c4o8u055e714.svg",
                                alt: "",
                                width: 74,
                                height: 71
                            }}
                            css={[
                                svgStyles,
                                checkmarkStyles,
                                sectionIsVisible && checkmarkAnimation
                            ]}
                            className="checkmark"
                        />

                        <h2 css={h2Styles}>
                            <FormattedMessage
                                id="HomePageTemplateV2.futureOfTeamworkHeader"
                                defaultMessage="<helpUsShape>Help us shape</helpUsShape> <theFutureOfTeamwork>the future of teamwork</theFutureOfTeamwork>"
                                values={{
                                    helpUsShape: HelpUsShapeHeading,
                                    theFutureOfTeamwork: FutureOfTeamworkHeading
                                }}
                            />
                        </h2>

                        <p css={pStyles} className=".body-lg">
                            <FormattedMessage
                                id="HomePageTemplateV2.futureOfTeamworkParagraph"
                                defaultMessage="At Atlassian, we believe the impossible is possible - together. Join us today to help us inspire teamwork anywhere and everywhere, worldwide."
                            />
                        </p>

                        <Adg4Button
                            variant="secondary"
                            color="black"
                            href={useLocalizedHref("/customers/advocacy")}
                            dataEventProps={{
                                "data-event": "clicked",
                                "data-event-component": "linkButton",
                                "data-event-container": "ctaSection",
                                "data-product-key": "home",
                                "data-label-english": "Share your story"
                            }}
                        >
                            <FormattedMessage
                                id="HomePageTemplateV2.futureOfTeamworkButton"
                                defaultMessage="Share your story"
                            />
                        </Adg4Button>
                    </div>
                </Row>
            </section>
        </UFOSegment>
    );
};

const fadeIn = keyframes({
    from: {
        opacity: 0
    },
    to: {
        opacity: 1
    }
});

const sectionStyles = css({
    position: "relative",
    overflow: "hidden",
    textAlign: "center",
    padding: "100px 0",
    maxWidth: "1440px",
    margin: "60px auto 0",

    [media.below.sm]: {
        padding: "140px 0 60px"
    }
});

const h2Styles = css({
    maxWidth: "510px",
    margin: 0
});

const pStyles = css({
    maxWidth: "460px",
    margin: 0
});

const reducedFontWeightStyle = css({
    fontWeight: "400 !important"
});

const svgStyles = css({
    position: "absolute",
    opacity: 0
});

const bigStarAnimation = css({
    animation: `${fadeIn} .6s ease-out .3s 1 forwards`
});

const smStarAnimation = css({
    animation: `${fadeIn} .3s ease-out .8s 1 forwards`
});

const checkmarkAnimation = css({
    animation: `${fadeIn} .4s ease-out 1.3s 1 forwards`
});

const bigStarStyles = css({
    insetBlockStart: "-94px",
    insetInlineStart: "-89px",

    [media.below.sm]: {
        insetInlineStart: "55px",
        insetBlockStart: "-139px"
    }
});

const smStarStyles = css({
    insetBlockStart: "21px",
    insetInlineStart: "-96px",

    [media.below.sm]: {
        insetInlineStart: "140px",
        insetBlockStart: "-72px"
    }
});

const checkmarkStyles = css({
    insetBlockStart: "48px",
    insetInlineEnd: "-102px",

    [media.below.sm]: {
        display: "none"
    }
});

const contentWrapperStyles = css({
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "32px",
    maxWidth: "568px",
    position: "relative"
});

FutureOfTeamwork.displayName = "FutureOfTeamwork";
